import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { fetchUserRoles, switchUserRole } from "../API_Service/Role_Slice";
import {
  employeeID,
  companyID,
  hasPrivilege,
  PRIVILEGES,
} from "../../Components/Common/UIComponents";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../API_Service/AxiosConfiguration";
import { useGlobalLoader } from "../Common/GlobalLoader";

// ─── Menu definition with required privileges ─────────────────────────────────
// privilege  : string or string[]  — ANY one match grants visibility
// If no privilege key, item is always visible (e.g. public/login pages)
const ALL_MENU_ITEMS = [
  {
    label: "Dashboard",
    icon: "dashboard",
    path: "/dashboard",
  },
  {
    label: "User Management",
    icon: "group",
    privilege: [
      PRIVILEGES.EMP_VIEW,
      PRIVILEGES.EMP_CREATE,
      PRIVILEGES.EMP_EDIT,
      PRIVILEGES.EMP_DELETE,
      PRIVILEGES.ROLE_VIEW,
      PRIVILEGES.ROLE_CREATE,
      PRIVILEGES.ROLE_EDIT,
      PRIVILEGES.ROLE_DELETE,
    ],
    submenu: [
      {
        label: "Associate",
        path: "/user-management",
        icon: "person",
        privilege: [
          PRIVILEGES.EMP_VIEW,
          PRIVILEGES.EMP_CREATE,
          PRIVILEGES.EMP_EDIT,
          PRIVILEGES.EMP_DELETE,
        ],
      },
      {
        label: "Roles and Responsibilities",
        path: "/role-management",
        icon: "manage_accounts",
        privilege: [
          PRIVILEGES.ROLE_VIEW,
          PRIVILEGES.ROLE_CREATE,
          PRIVILEGES.ROLE_EDIT,
          PRIVILEGES.ROLE_DELETE,
        ],
      },
      {
        label: "Shift Management",
        path: "/shift-mapping",
        icon: "schedule",
        privilege: [
          PRIVILEGES.SU_VIEW,
          PRIVILEGES.SU_CREATE,
          PRIVILEGES.SU_EDIT,
        ],
      },
    ],
  },
  {
    label: "Project Management",
    icon: "account_tree",
    path: "/project-management",
    privilege: [
      PRIVILEGES.PROJ_VIEW,
      PRIVILEGES.PROJ_CREATE,
      PRIVILEGES.PROJ_EDIT,
    ],
  },
  {
    label: "Lab Management",
    icon: "biotech",
    // privilege: [
    //   PRIVILEGES.LAB_VIEW,
    //   PRIVILEGES.LAB_CREATE,
    //   PRIVILEGES.LAB_EDIT,
    // ],
    submenu: [
      {
        label: "Lab Booking",
        path: "/lab-management/lab-Booking",
        icon: "event",
        privilege: [
          PRIVILEGES.LAB_VIEW,
          PRIVILEGES.LAB_CREATE,
          PRIVILEGES.LAB_EDIT,
        ],
      },
      {
        label: "Lab Booking Requests",
        path: "/lab-management/labBooking-requests",
        icon: "fact_check",
        privilege: [
          PRIVILEGES.LAB_VIEW,
          PRIVILEGES.LABREQ_VIEW,
          PRIVILEGES.LABREQ_APPROVE,
        ],
      },
      {
        label: "Lab Utilization",
        path: "/lab-management/setup-lab-utilization",
        icon: "science",
        privilege: [
          PRIVILEGES.SU_VIEW,
          PRIVILEGES.SU_CREATE,
          PRIVILEGES.SU_EDIT,
        ],
      },
      {
        label: "Measurement Protocol",
        path: "/lab-management/measurement-protocol",
        icon: "assignment",
        privilege: [PRIVILEGES.MP_VIEW],
      },
    ],
  },
  {
    label: "Email Template",
    icon: "email",
    path: "/email-templateindex",
    privilege: [PRIVILEGES.EMAIL_VIEW, PRIVILEGES.EMAIL_EDIT],
  },
  {
    label: "Reports",
    icon: "assessment",
    submenu: [
      {
        label: "Setup Utilization Report",
        path: "/reports/lab-utilization",
        icon: "analytics",
      },
       {
        label: "Shift Changeover Report",
        path: "/reports/shift-changeover",
        icon: "analytics",
      },
    ],
  },
  {
    label: "Application Configuration",
    icon: "settings_input_component",
    path: "/application-config",
    privilege: ["APPCONFIGVIEW"],
    //
  },
];

// ─── Privilege filter helpers ─────────────────────────────────────────────────
function canSee(privs) {
  if (!privs) return true; // no restriction
  const list = Array.isArray(privs) ? privs : [privs];
  return list.some((p) => hasPrivilege(p));
}

function filterMenu(items) {
  const visible = items
    .filter((item) => canSee(item.privilege))
    .map((item) => {
      if (!item.submenu) return item;
      const visibleSubs = item.submenu.filter((s) => canSee(s.privilege));
      return { ...item, submenu: visibleSubs };
    })
    .filter((item) => !item.submenu || item.submenu.length > 0);

  // Embedded-from-Smile sessions only need Lab Management — hide everything else
  if (localStorage.getItem("isFromSmile") === "1") {
    return visible.filter((item) => item.label === "Lab Management");
  }
  return visible;
}

// ─────────────────────────────────────────────────────────────────────────────

const SideNav = ({ collapsed = false, onToggle }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userRoles, userRolesLoading, activeRole, switchLoading } =
    useSelector((state) => state.role);

  const [hoveredItem, setHoveredItem] = useState(null);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(() => {
    const id = localStorage.getItem("roleId");
    const name = localStorage.getItem("roleName");
    return id && name ? { RoleID: parseInt(id), RoleName: name } : null;
  });
  const roleDropdownRef = useRef(null);
  const leaveTimer = useRef(null);
  const { showLoader, hideLoader } = useGlobalLoader();
  const [showBackToSmile] = useState(
    () => localStorage.getItem("isFromSmile") === "1",
  );

  // Re-filter whenever privileges in localStorage change (e.g. after role switch)
  const [menuItems, setMenuItems] = useState(() => filterMenu(ALL_MENU_ITEMS));

  useEffect(() => {
    const empId = employeeID || localStorage.getItem("empid");
    if (empId) {
      dispatch(fetchUserRoles(empId));
    }
  }, [dispatch]);

  useEffect(() => {
    if (userRoles.length > 0 && !selectedRole) {
      const loggedInRoleId = localStorage.getItem("roleId");
      const matched = loggedInRoleId
        ? userRoles.find((r) => r.RoleID === parseInt(loggedInRoleId))
        : userRoles[0];
      if (matched) setSelectedRole(matched);
    }
  }, [userRoles]);

  // Re-filter menu when role changes (privileges update in localStorage)
  useEffect(() => {
    const onRoleChanged = () => setMenuItems(filterMenu(ALL_MENU_ITEMS));
    window.addEventListener("roleChanged", onRoleChanged);
    return () => window.removeEventListener("roleChanged", onRoleChanged);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => pathname === path;

  const isSubmenuActive = (item) =>
    item.submenu?.some((s) => pathname.startsWith(s.path));

  // ── Hover handlers with small delay so submenu stays open ──
  const handleParentEnter = (label) => {
    clearTimeout(leaveTimer.current);
    setHoveredItem(label);
  };

  const handleParentLeave = () => {
    leaveTimer.current = setTimeout(() => setHoveredItem(null), 120);
  };

  const handleSubmenuEnter = () => {
    clearTimeout(leaveTimer.current);
  };

  const handleSubmenuLeave = () => {
    leaveTimer.current = setTimeout(() => setHoveredItem(null), 120);
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setIsRoleDropdownOpen(false);

    localStorage.setItem("roleId", role.RoleID);
    localStorage.setItem("roleName", role.RoleName);
    localStorage.setItem("roleCode", role.RoleCode);
    const empId = employeeID || localStorage.getItem("empid");
    dispatch(switchUserRole({ employeeId: empId, roleId: role.RoleID })).then(
      () => {
        window.dispatchEvent(new CustomEvent("roleChanged", { detail: role }));
      },
    );
  };

  // When collapsed but hovered, temporarily show expanded layout
  const isCollapsed = collapsed && !sidebarHovered;

  return (
    <aside
      className="fixed left-0 top-0 h-full z-50 transition-all duration-300 ease-in-out"
      style={{ width: isCollapsed ? "72px" : "256px" }}
      onMouseEnter={() => setSidebarHovered(true)}
      onMouseLeave={() => setSidebarHovered(false)}
    >
      {/* Inner container — clips overflowing text but not the toggle button */}
      <div className="relative h-full bg-white border-r border-slate-100 shadow-[2px_0_12px_rgba(0,0,0,0.06)] flex flex-col overflow-hidden">
        {/* ── Logo header ── */}
        <div className="flex items-center h-16 px-4 border-b border-slate-100 flex-shrink-0">
          {isCollapsed ? (
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#08757C] to-[#005b60] flex items-center justify-center text-white font-black text-sm mx-auto select-none">
              LP
            </div>
          ) : (
            <div className="min-w-0">
              <h1 className="text-[15px] font-black text-[#08757C] leading-tight tracking-tight">
                Lab Planning
              </h1>
              
            </div>
          )}
        </div>

        {/* ── Role selector — hidden when collapsed or embedded-from-Smile ── */}
        {!isCollapsed &&
          userRoles.length > 0 &&
          localStorage.getItem("isFromSmile") !== "1" && (
            <div className="px-3 pt-3 pb-2 flex-shrink-0" ref={roleDropdownRef}>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 px-1">
                Current Role
              </p>
              <div className="relative">
                <button
                  onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl hover:border-[#08757C]/40 hover:bg-white transition-all text-left"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-5 h-5 rounded-lg bg-[#08757C]/10 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-[13px] text-[#08757C]">
                        badge
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-slate-700 truncate">
                      {selectedRole?.RoleName || "Select Role"}
                    </span>
                  </div>
                  <span
                    className={`material-symbols-outlined text-[16px] text-slate-400 transition-transform duration-200 flex-shrink-0 ${isRoleDropdownOpen ? "rotate-180" : ""}`}
                  >
                    expand_more
                  </span>
                </button>
                {isRoleDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-100 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto py-1">
                    {userRolesLoading ? (
                      <div className="px-3 py-2 text-xs text-slate-400">
                        Loading roles…
                      </div>
                    ) : (
                      userRoles.map((role) => (
                        <button
                          key={role.RoleID}
                          onClick={() => handleRoleChange(role)}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors flex items-center gap-2 ${selectedRole?.RoleID === role.RoleID ? "text-[#08757C] font-bold" : "text-slate-600"}`}
                        >
                          <span className="material-symbols-outlined text-[14px] text-[#08757C]">
                            {selectedRole?.RoleID === role.RoleID
                              ? "radio_button_checked"
                              : "radio_button_unchecked"}
                          </span>
                          {role.RoleName}
                        </button>
                      ))
                    )}
                    {switchLoading && (
                      <div className="px-3 py-1 text-xs text-[#08757C] border-t border-slate-100">
                        Switching…
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        {!isCollapsed && (
          <div className="h-px bg-slate-100 mx-3 flex-shrink-0" />
        )}

        {/* ── Nav menu ── */}
        <nav className="flex flex-col gap-0.5 flex-1 overflow-y-auto p-2">
          {!isCollapsed && (
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest px-2 pt-1 pb-1.5">
              Menu
            </p>
          )}
          {menuItems.map((item) => {
            const hasSubmenu = !!item.submenu;
            const parentActive = hasSubmenu
              ? isSubmenuActive(item)
              : isActive(item.path);
            const isOpen = hoveredItem === item.label;

            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleParentEnter(item.label)}
                onMouseLeave={() =>
                  hasSubmenu ? handleParentLeave() : setHoveredItem(null)
                }
              >
                {item.path ? (
                  <NavLink
                    to={item.path}
                    className={({ isActive: a }) =>
                      `flex items-center gap-2.5 rounded-xl text-sm transition-all duration-150 group
                       ${isCollapsed ? "justify-center p-2.5" : "px-3 py-2"}
                       ${
                         a
                           ? "bg-[#08757C] text-white shadow-md shadow-[#08757C]/20 font-semibold"
                           : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                       }`
                    }
                  >
                    {({ isActive: a }) => (
                      <>
                        <span
                          className="material-symbols-outlined text-[19px] flex-shrink-0"
                          style={a ? { fontVariationSettings: "'FILL' 1" } : {}}
                        >
                          {item.icon}
                        </span>
                        {!isCollapsed && (
                          <span className="truncate text-[13px]">
                            {item.label}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ) : (
                  <div
                    className={`flex items-center gap-2.5 rounded-xl text-sm cursor-default transition-all duration-150
                    ${isCollapsed ? "justify-center p-2.5" : "px-3 py-2 justify-between"}
                    ${
                      parentActive
                        ? "bg-[#08757C]/8 text-[#08757C] font-semibold"
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2.5 ${isCollapsed ? "justify-center" : ""}`}
                    >
                      <span
                        className="material-symbols-outlined text-[19px] flex-shrink-0"
                        style={
                          parentActive
                            ? { fontVariationSettings: "'FILL' 1" }
                            : {}
                        }
                      >
                        {item.icon}
                      </span>
                      {!isCollapsed && (
                        <span className="truncate text-[13px]">
                          {item.label}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && hasSubmenu && (
                      <span
                        className={`material-symbols-outlined text-[15px] flex-shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                      >
                        chevron_right
                      </span>
                    )}
                  </div>
                )}

                {/* Expanded: inline submenu */}
                {!isCollapsed && hasSubmenu && isOpen && (
                  <div
                    onMouseEnter={handleSubmenuEnter}
                    onMouseLeave={handleSubmenuLeave}
                    className="mt-0.5 ml-4 border-l-2 border-slate-100 pl-2 flex flex-col gap-0.5 pb-0.5"
                  >
                    {item.submenu.map((sub) => {
                      const subActive = pathname.startsWith(sub.path);
                      return (
                        <NavLink
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setHoveredItem(null)}
                          className={`flex items-center gap-2 px-2.5 py-1.5 text-[12px] rounded-lg transition-colors
                            ${
                              subActive
                                ? "bg-[#08757C]/10 text-[#08757C] font-semibold"
                                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                            }`}
                        >
                          <span
                            className="material-symbols-outlined text-[14px] flex-shrink-0"
                            style={
                              subActive
                                ? { fontVariationSettings: "'FILL' 1" }
                                : {}
                            }
                          >
                            {sub.icon}
                          </span>
                          <span>{sub.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}

                {/* Collapsed: flyout panel */}
                {isCollapsed && isOpen && (
                  <div
                    onMouseEnter={handleSubmenuEnter}
                    onMouseLeave={handleSubmenuLeave}
                    className="absolute left-full top-0 ml-3 z-50"
                    style={{ minWidth: "176px" }}
                  >
                    <div className="bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-200/80 overflow-hidden">
                      <div className="px-3 py-2.5 border-b border-slate-50 bg-slate-50/80">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          {item.label}
                        </p>
                      </div>
                      <div className="p-1.5 flex flex-col gap-0.5">
                        {(hasSubmenu ? item.submenu : []).map((sub) => {
                          const subActive = pathname.startsWith(sub.path);
                          return (
                            <NavLink
                              key={sub.path}
                              to={sub.path}
                              onClick={() => setHoveredItem(null)}
                              className={`flex items-center gap-2 px-2.5 py-2 text-xs rounded-xl transition-colors
                                ${
                                  subActive
                                    ? "bg-[#08757C] text-white font-semibold shadow-sm"
                                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                }`}
                            >
                              <span
                                className="material-symbols-outlined text-[15px] flex-shrink-0"
                                style={
                                  subActive
                                    ? { fontVariationSettings: "'FILL' 1" }
                                    : {}
                                }
                              >
                                {sub.icon}
                              </span>
                              {sub.label}
                            </NavLink>
                          );
                        })}
                        {!hasSubmenu && (
                          <NavLink
                            to={item.path}
                            onClick={() => setHoveredItem(null)}
                            className={({ isActive: a }) =>
                              `flex items-center gap-2 px-2.5 py-2 text-xs rounded-xl transition-colors
                               ${a ? "bg-[#08757C] text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`
                            }
                          >
                            <span className="material-symbols-outlined text-[15px]">
                              {item.icon}
                            </span>
                            {item.label}
                          </NavLink>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Back to Smile — shown only when opened from Smile (isFromSmile = 1) ── */}
        {showBackToSmile && (
          <div className="flex-shrink-0 border-t border-slate-100 p-3">
            <button
              type="button"
              title="Back/Go to Smile"
              onClick={async () => {
                showLoader();
                const smileUrl = localStorage.getItem("smileUrl");
                const token = localStorage.getItem("token");
                localStorage.removeItem("isFromSmile");
                localStorage.removeItem("smileUrl");

                try {
                  const res = await api.post(
                    "Auth/BackToSmile",
                    { returnUrl: smileUrl || null },
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  if (res?.data?.redirectUrl) {
                    window.location.href = res.data.redirectUrl;
                    return;
                  }
                } catch {
                  /* fall back to a plain redirect below */
                }

                if (smileUrl) {
                  window.location.href = smileUrl;
                } else {
                  hideLoader();
                }
              }}
              className={`w-full flex items-center gap-2.5 py-2.5 rounded-xl text-white text-sm font-bold
                         bg-gradient-to-br from-[#08757C] to-[#005b60] shadow-md shadow-[#08757C]/25
                         hover:opacity-90 hover:shadow-lg active:scale-95 transition-all
                         ${isCollapsed ? "justify-center px-0" : "justify-center px-3"}`}
            >
              <span className="material-symbols-outlined text-[19px] flex-shrink-0">
                arrow_back
              </span>
              {!isCollapsed && (
                <span className="truncate">Back/Go to Smile</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Floating toggle button — sits at the right edge ── */}
      <button
        onClick={onToggle}
        className="absolute top-14 -right-3 z-10 w-6 h-6 bg-white border border-slate-200 rounded-full shadow-md flex items-center justify-center text-slate-400 hover:text-[#08757C] hover:border-[#08757C]/40 hover:shadow-lg transition-all duration-200"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <span
          className="material-symbols-outlined transition-transform duration-300"
          style={{
            fontSize: "14px",
            transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
          }}
        >
          chevron_right
        </span>
      </button>
    </aside>
  );
};

export default SideNav;
